'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, Sparkles, Star, ArrowRight, ChevronRight, Heart, Shield, Clock } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface BeautyService {
  id: string;name: string;category: string;location: string;price_from: number;
  rating: number;reviews: number;image_url: string;is_featured: boolean;
  provider: string;verified: boolean;duration: string;tags: string[];
}

const SERVICES: BeautyService[] = [
{ id: '1', name: 'Full Hair Braiding & Styling', category: 'Hair', location: 'Nairobi CBD', price_from: 1500, rating: 4.9, reviews: 128, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0cdd2fd-1775395360193.png", is_featured: true, provider: 'Amina Beauty Studio', verified: true, duration: '3–4 hrs', tags: ['Braids', 'Weaves', 'Natural'] },
{ id: '2', name: 'Gel Manicure & Pedicure', category: 'Nails', location: 'Westlands', price_from: 800, rating: 4.7, reviews: 89, image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_184f0c073-1784530799188.png', is_featured: false, provider: 'Glam Nails', verified: true, duration: '1.5 hrs', tags: ['Gel', 'Acrylic', 'Art'] },
{ id: '3', name: 'Bridal Makeup Package', category: 'Makeup', location: 'Kilimani', price_from: 8000, rating: 5.0, reviews: 45, image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1116a4ec5-1776348985536.png', is_featured: true, provider: 'Grace Artistry', verified: true, duration: '2–3 hrs', tags: ['Bridal', 'Airbrush', 'HD'] },
{ id: '4', name: 'Deep Tissue Massage', category: 'Spa', location: 'Mombasa', price_from: 2500, rating: 4.8, reviews: 67, image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1246d41fb-1764802362452.png', is_featured: false, provider: 'Serenity Spa', verified: false, duration: '60 min', tags: ['Massage', 'Relaxation', 'Therapy'] },
{ id: '5', name: 'Lash Extensions (Classic)', category: 'Lashes', location: 'Nairobi', price_from: 2000, rating: 4.6, reviews: 112, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef970165-1772845158588.png", is_featured: false, provider: 'Lash Lab Kenya', verified: true, duration: '2 hrs', tags: ['Classic', 'Volume', 'Hybrid'] },
{ id: '6', name: 'Hair Relaxer & Treatment', category: 'Hair', location: 'Kisumu', price_from: 1200, rating: 4.5, reviews: 34, image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1116a4ec5-1776348985536.png', is_featured: false, provider: 'Style Hub', verified: false, duration: '2 hrs', tags: ['Relaxer', 'Treatment', 'Keratin'] }];


const CATEGORIES = ['All', 'Hair', 'Nails', 'Makeup', 'Spa', 'Lashes', 'Skincare', 'Brows'];
const LOCATIONS = ['All Locations', 'Nairobi CBD', 'Westlands', 'Kilimani', 'Mombasa', 'Kisumu'];
const PRICE_RANGES = ['Any Price', 'Under KES 1K', 'KES 1K–3K', 'KES 3K–8K', 'KES 8K+'];

export default function BeautyLandingPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [location, setLocation] = useState('All Locations');
  const [priceRange, setPriceRange] = useState('Any Price');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const filtered = SERVICES.filter((s) => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.provider.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || s.category === category;
    const matchLoc = location === 'All Locations' || s.location.includes(location);
    let matchPrice = true;
    if (priceRange === 'Under KES 1K') matchPrice = s.price_from < 1000;else
    if (priceRange === 'KES 1K–3K') matchPrice = s.price_from >= 1000 && s.price_from <= 3000;else
    if (priceRange === 'KES 3K–8K') matchPrice = s.price_from > 3000 && s.price_from <= 8000;else
    if (priceRange === 'KES 8K+') matchPrice = s.price_from > 8000;
    return matchSearch && matchCat && matchLoc && matchPrice;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0a14 0%, #0d0a1a 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #E91E63 0%, transparent 50%), radial-gradient(circle at 75% 30%, #FF4081 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
            style={{ background: 'rgba(233,30,99,0.15)', color: '#E91E63', border: '1px solid rgba(233,30,99,0.3)' }}>
              <Sparkles size={12} /> {3200 .toLocaleString()} Beauty Services
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Book <span style={{ color: '#E91E63' }}>Beauty Services</span> Near You
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Hair, nails, makeup, spa and more. Verified professionals across Kenya.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search services or salons…"
              className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        {/* Category pills */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((c) =>
            <button key={c} onClick={() => setCategory(c)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: category === c ? 'rgba(233,30,99,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#E91E63' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(233,30,99,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            <select value={location} onChange={(e) => setLocation(e.target.value)}
            className="px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
              {LOCATIONS.map((l) => <option key={l} value={l} style={{ background: '#0D1526' }}>{l}</option>)}
            </select>
            <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)}
            className="px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
              {PRICE_RANGES.map((p) => <option key={p} value={p} style={{ background: '#0D1526' }}>{p}</option>)}
            </select>
          </div>

          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#E91E63', fontWeight: 700 }}>{filtered.length}</span> services found
            </span>
            <Link href="/login" className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
            style={{ background: '#E91E63', color: '#fff' }}>
              List Your Service <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((s) =>
            <div key={s.id} className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${s.is_featured ? 'rgba(233,30,99,0.25)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="relative h-40">
                  <img src={s.image_url} alt={`${s.name} beauty service in ${s.location} by ${s.provider}`}
                className="w-full h-full object-cover" loading="lazy" />
                  {s.is_featured &&
                <span className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                style={{ background: '#E91E63', color: '#fff' }}>Featured</span>
                }
                  <button onClick={() => toggleSave(s.id)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: savedIds.has(s.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                    <Heart size={13} fill={savedIds.has(s.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                  </button>
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-700 text-white line-clamp-1">{s.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{s.provider}</p>
                        {s.verified && <Shield size={9} style={{ color: '#00C853' }} />}
                      </div>
                    </div>
                    <p className="text-sm font-800 flex-shrink-0" style={{ color: '#E91E63' }}>from KES {s.price_from.toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1">
                      <Star size={10} fill="#FFB300" style={{ color: '#FFB300' }} />
                      <span className="text-xs font-700" style={{ color: '#FFB300' }}>{s.rating}</span>
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>({s.reviews})</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{s.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{s.location}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {s.tags.map((t) =>
                  <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-md font-600"
                  style={{ background: 'rgba(233,30,99,0.12)', color: '#FF80AB' }}>{t}</span>
                  )}
                  </div>
                  <Link href="/login" className="mt-3 flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-700"
                style={{ background: 'rgba(233,30,99,0.12)', color: '#E91E63', border: '1px solid rgba(233,30,99,0.2)' }}>
                    Book Now <ChevronRight size={11} />
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(233,30,99,0.1), rgba(255,64,129,0.1))', border: '1px solid rgba(233,30,99,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {3200 .toLocaleString()} Services</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up to book, save favourites, and get exclusive deals.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#E91E63', color: '#fff' }}>
                <Sparkles size={16} /> Book a Service
              </Link>
              <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                List Your Business
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>);

}